import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import MasterJobService from '../../services/masterJobService';
import MasterJobForm from './MasterJobForm';
import MasterJobImport from './MasterJobImport';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import DeleteConfirmationDialog from '../../components/common/DeleteConfirmationDialog';

const MasterJobList = () => {
  // Estado para los datos y la UI
  const [masterJobs, setMasterJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estado para paginación y filtros
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [sortBy, setSortBy] = useState('kode_pekerjaan');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Estado para modales
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedMasterJob, setSelectedMasterJob] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  
  // Autenticación
  const { user } = useAuth();
  const isCommercial = user && ['commercial_manager', 'commercial_staff'].includes(user.role);
  
  // Función para cargar datos
  const loadMasterJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await MasterJobService.getAllMasterJobs(
        page + 1, 
        rowsPerPage, 
        searchQuery, 
        category,
        sortBy,
        sortOrder
      );
      
      if (response.success) {
        setMasterJobs(response.data);
        setTotalItems(response.pagination.total);
      } else {
        setError('Error loading data');
        toast.error('Could not load work item data');
      }
    } catch (err) {
      console.error('Error loading work items:', err);
      setError('Error loading data');
      toast.error('Could not load data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  // Función para cargar categorías
  const loadCategories = async () => {
    try {
      const response = await MasterJobService.getCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (err) {
      console.error('Error cargando categorías:', err);
    }
  };
  
  // Cargar datos iniciales
  useEffect(() => {
    if (!isCommercial) {
      setError('Acceso no autorizado');
      return;
    }
    
    loadCategories();
  }, [isCommercial]);
  
  // Cargar jobs cuando cambian los filtros o la paginación
  useEffect(() => {
    if (isCommercial) {
      loadMasterJobs();
    }
  }, [page, rowsPerPage, sortBy, sortOrder, category, isCommercial]);
  
  // Función para buscar
  const handleSearch = () => {
    setPage(0);
    loadMasterJobs();
  };
  
  // Funciones para la paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Funciones para el formulario
  const handleOpenCreateForm = () => {
    setSelectedMasterJob(null);
    setFormMode('create');
    setFormOpen(true);
  };
  
  const handleOpenEditForm = (masterJob) => {
    setSelectedMasterJob(masterJob);
    setFormMode('edit');
    setFormOpen(true);
  };
  
  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedMasterJob(null);
  };
  
  const handleFormSubmit = async (formData) => {
    try {
      let response;
      
      if (formMode === 'create') {
        response = await MasterJobService.createMasterJob(formData);
        if (response.success) {
          toast.success('Work item created successfully');
        }
      } else {
        response = await MasterJobService.updateMasterJob(selectedMasterJob.id, formData);
        if (response.success) {
          toast.success('Work item updated successfully');
        }
      }
      
      // Reload categories after successful creation/update to include any new categories
      loadCategories();
      
      handleCloseForm();
      loadMasterJobs();
    } catch (err) {
      console.error('Error in form submission:', err);
      toast.error('Error: ' + (err.message || 'Unknown error'));
    }
  };
  
  // Funciones para eliminar
  const handleOpenDeleteDialog = (masterJob) => {
    setJobToDelete(masterJob);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setJobToDelete(null);
  };
  
  const handleDelete = async () => {
    if (!jobToDelete) return;
    
    try {
      const response = await MasterJobService.deleteMasterJob(jobToDelete.id);
      if (response.success) {
        toast.success('Work item deleted successfully');
        loadMasterJobs();
      } else {
        toast.error('Could not delete the work item');
      }
    } catch (err) {
      console.error('Error deleting work item:', err);
      toast.error('Error: ' + (err.message || 'Unknown error'));
    } finally {
      handleCloseDeleteDialog();
    }
  };
  
  // Funciones para importar/exportar
  const handleOpenImport = () => {
    setImportOpen(true);
  };
  
  const handleCloseImport = () => {
    setImportOpen(false);
  };
  
  const handleImportSuccess = () => {
    handleCloseImport();
    loadMasterJobs();
    loadCategories(); // Also reload categories after import
    toast.success('Data imported successfully');
  };
  
  const handleExportToExcel = () => {
    try {
      // Preparar datos para exportar
      const dataToExport = masterJobs.map(job => ({
        'Kode Pekerjaan': job.kode_pekerjaan,
        'Nama Pekerjaan': job.nama_pekerjaan,
        'Kategori/Sub Divisi': job.kategori,
        'Volume Default': job.volume_default,
        'Satuan (BoQ)': job.satuan,
        'Tipe Input': job.tipe_input
      }));
      
      // Crear worksheet
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      
      // Configurar anchos de columna
      const wscols = [
        { wch: 15 },  // Kode Pekerjaan
        { wch: 35 },  // Nama Pekerjaan
        { wch: 20 },  // Kategori
        { wch: 15 },  // Volume Default
        { wch: 15 },  // Satuan
        { wch: 15 }   // Tipe Input
      ];
      worksheet['!cols'] = wscols;
      
      // Crear workbook y añadir worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Master Jobs');
      
      // Generar archivo Excel
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Guardar archivo
      saveAs(blob, 'master_jobs_export.xlsx');
      
      toast.success('Datos exportados correctamente');
    } catch (err) {
      console.error('Error exportando a Excel:', err);
      toast.error('Error exportando datos: ' + (err.message || 'Error desconocido'));
    }
  };
  
  // Mostrar mensaje de acceso no autorizado
  if (!isCommercial) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page. This page is exclusive to users with Commercial role.
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: '#5D4037' }}>
          Work Items Master List
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateForm}
            sx={{ 
              bgcolor: '#F0C14B', 
              color: '#5D4037',
              '&:hover': { bgcolor: '#DDA726' }
            }}
          >
            Add Work Item
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={handleOpenImport}
            sx={{ 
              color: '#5D4037', 
              borderColor: '#5D4037',
              '&:hover': { 
                bgcolor: '#F9F5EB',
                borderColor: '#8D6E63'
              }
            }}
          >
            Import Excel
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportToExcel}
            sx={{ 
              color: '#5D4037', 
              borderColor: '#5D4037',
              '&:hover': { 
                bgcolor: '#F9F5EB',
                borderColor: '#8D6E63'
              }
            }}
          >
            Export Excel
          </Button>
        </Box>
      </Box>
      
      <Paper elevation={0} sx={{ mb: 3, p: 2, bgcolor: '#FFFFFF' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              variant="outlined"
              label="Search"
              placeholder="Search by code or work item name"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              variant="outlined"
              label="Category"
              size="small"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              variant="outlined"
              label="Sort by"
              size="small"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="kode_pekerjaan">Work Item Code</MenuItem>
              <MenuItem value="nama_pekerjaan">Work Item Name</MenuItem>
              <MenuItem value="kategori">Category</MenuItem>
              <MenuItem value="createdAt">Created Date</MenuItem>
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={2}>
            <TextField
              select
              fullWidth
              variant="outlined"
              label="Order"
              size="small"
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </TextField>
          </Grid>
        </Grid>
        
        <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              setSearchQuery('');
              setCategory('');
              setSortBy('kode_pekerjaan');
              setSortOrder('asc');
              setPage(0);
              loadMasterJobs();
            }}
            size="small"
          >
            Reset Filter
          </Button>
        </Grid>
      </Paper>
      
      <Paper elevation={0} sx={{ bgcolor: '#FFFFFF' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress sx={{ color: '#F0C14B' }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : masterJobs.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No data available. Use the "Add Work Item" button to add new work items or import from Excel.
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#FFF8E1' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: '#5D4037' }}>Work Item Code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#5D4037' }}>Work Item Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#5D4037' }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#5D4037' }}>Default Volume</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#5D4037' }}>Unit</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#5D4037' }}>Input Type</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#5D4037' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {masterJobs.map((job) => (
                    <TableRow key={job.id} hover>
                      <TableCell>{job.kode_pekerjaan}</TableCell>
                      <TableCell>{job.nama_pekerjaan}</TableCell>
                      <TableCell>
                        <Chip 
                          label={job.kategori} 
                          size="small" 
                          sx={{ 
                            bgcolor: '#FFF8E1', 
                            color: '#5D4037',
                            borderRadius: '4px',
                            fontWeight: 'medium'
                          }} 
                        />
                      </TableCell>
                      <TableCell>{job.volume_default}</TableCell>
                      <TableCell>{job.satuan}</TableCell>
                      <TableCell>
                        <Chip 
                          label={job.tipe_input} 
                          size="small"
                          color={job.tipe_input === 'autofill' ? 'primary' : 'default'}
                          sx={{ 
                            bgcolor: job.tipe_input === 'autofill' ? '#E3F2FD' : '#F5F5F5',
                            color: job.tipe_input === 'autofill' ? '#1976D2' : '#757575'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenEditForm(job)}
                            sx={{ color: '#F0C14B' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDeleteDialog(job)}
                            sx={{ color: '#FF7043' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              labelRowsPerPage="Rows per page:"
            />
          </>
        )}
      </Paper>
      
      {/* Modal de formulario */}
      {formOpen && (
        <MasterJobForm
          open={formOpen}
          onClose={handleCloseForm}
          onSubmit={handleFormSubmit}
          masterJob={selectedMasterJob}
          mode={formMode}
          categories={categories}
        />
      )}
      
      {/* Modal de importación */}
      {importOpen && (
        <MasterJobImport
          open={importOpen}
          onClose={handleCloseImport}
          onSuccess={handleImportSuccess}
        />
      )}
      
      {/* Diálogo de confirmación de eliminación */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDelete}
        title="Delete Work Item"
        content={`Are you sure you want to delete the work item "${jobToDelete?.nama_pekerjaan}" (${jobToDelete?.kode_pekerjaan})? This action cannot be undone.`}
      />
    </Box>
  );
};

export default MasterJobList; 